import { type Company } from "@/types";

export type PlanType = 'free' | 'starter' | 'pro' | 'enterprise';

export const PLAN_LIMITS = {
  free: {
    feedbackItems: 10,
    boards: 1,
    roadmaps: 1,
    teamMembers: 1,
    customDomain: false,
    aiChat: false,
    changelogEmails: false,
    branding: 'platform', // Shows "powered by" branding
  },
  starter: {
    feedbackItems: Infinity,
    boards: 3,
    roadmaps: 3,
    teamMembers: 3,
    customDomain: true,
    aiChat: true,
    changelogEmails: true,
    branding: 'custom',
  },
  pro: {
    feedbackItems: Infinity,
    boards: 3,
    roadmaps: 3,
    teamMembers: 3,
    customDomain: true,
    aiChat: true,
    changelogEmails: true,
    branding: 'custom',
  },
  enterprise: {
    feedbackItems: Infinity,
    boards: Infinity,
    roadmaps: Infinity,
    teamMembers: 5,
    customDomain: true,
    aiChat: true,
    changelogEmails: true,
    branding: 'custom',
  },
} as const;

export const getPlanLimits = (plan: string) => {
  const normalizedPlan = plan.toLowerCase() as PlanType;
  return PLAN_LIMITS[normalizedPlan] || PLAN_LIMITS.free;
};

export const canCreateFeedback = (plan: string, currentCount: number): { allowed: boolean; message?: string } => {
  const limits = getPlanLimits(plan);
  if (currentCount >= limits.feedbackItems) {
    return {
      allowed: false,
      message: `You've reached the limit of ${limits.feedbackItems} feedback items on the Free plan. Upgrade to get unlimited feedback items.`,
    };
  }
  return { allowed: true };
};

export const canCreateBoard = (plan: string, currentCount: number): { allowed: boolean; message?: string } => {
  const limits = getPlanLimits(plan);
  if (currentCount >= limits.boards) {
    const upgradeMessage = plan === 'free' 
      ? 'Upgrade to Starter to create up to 3 boards or Enterprise for unlimited boards.'
      : 'Upgrade to Enterprise to create unlimited boards.';
    return {
      allowed: false,
      message: `You've reached the limit of ${limits.boards} feedback board(s). ${upgradeMessage}`,
    };
  }
  return { allowed: true };
};

export const canCreateRoadmap = (plan: string, currentCount: number): { allowed: boolean; message?: string } => {
  const limits = getPlanLimits(plan);
  if (currentCount >= limits.roadmaps) {
    const upgradeMessage = plan === 'free'
      ? 'Upgrade to Starter to create up to 3 roadmaps or Enterprise for unlimited roadmaps.'
      : 'Upgrade to Enterprise to create unlimited roadmaps.';
    return {
      allowed: false,
      message: `You've reached the limit of ${limits.roadmaps} roadmap(s). ${upgradeMessage}`,
    };
  }
  return { allowed: true };
};

export const canInviteTeamMember = (plan: string, currentCount: number): { allowed: boolean; message?: string } => {
  const limits = getPlanLimits(plan);
  if (currentCount >= limits.teamMembers) {
    const upgradeMessage = plan === 'free'
      ? 'Upgrade to Starter (3 members) or Enterprise (5 members) to add more team members.'
      : plan === 'starter' || plan === 'pro'
      ? 'Upgrade to Enterprise for up to 5 team members, or purchase additional members at $10/month each.'
      : 'You can purchase additional members at $10/month each.';
    return {
      allowed: false,
      message: `You've reached the limit of ${limits.teamMembers} team member(s). ${upgradeMessage}`,
    };
  }
  return { allowed: true };
};

export const canUseAIChat = (plan: string): { allowed: boolean; message?: string } => {
  const limits = getPlanLimits(plan);
  if (!limits.aiChat) {
    return {
      allowed: false,
      message: 'AI Live Chat Integration is available on Starter and Enterprise plans. Upgrade to enable this feature.',
    };
  }
  return { allowed: true };
};

export const canUseCustomDomain = (plan: string): { allowed: boolean; message?: string } => {
  const limits = getPlanLimits(plan);
  if (!limits.customDomain) {
    return {
      allowed: false,
      message: 'Custom domains are available on Starter and Enterprise plans. Upgrade to use your own domain.',
    };
  }
  return { allowed: true };
};

export const canUseChangelogEmails = (plan: string): { allowed: boolean; message?: string } => {
  const limits = getPlanLimits(plan);
  if (!limits.changelogEmails) {
    return {
      allowed: false,
      message: 'Changelog email notifications are available on Starter and Enterprise plans. Upgrade to notify your users.',
    };
  }
  return { allowed: true };
};

export const getUpgradeUrl = (currentPlan: string): string => {
  // Navigate to plan tab
  return '/admin?tab=plan';
};
